/**
 * Where a candidate is in a regular test: the open question, the questions
 * they flagged and the options they struck out. Kept in this browser so a
 * reload returns to the same question with its flags. The question is kept by id, so a shuffled order still finds
 * it. Storage can be blocked (private mode, full quota); the test then resumes
 * at the first unanswered question, as before.
 */
export interface ResumePosition {
  questionId: string | null;
  flagged: string[];
  /** Struck-out option ids, by question id (answer eliminator). */
  eliminated?: Record<string, string[]>;
}

interface LoadedPosition {
  index: number | null;
  flagged: string[];
  eliminated: Record<string, string[]>;
}

const EMPTY: LoadedPosition = { index: null, flagged: [], eliminated: {} };

const storageKey = (attemptId: string) => `examing.attempt-position.${attemptId}`;

/** The saved question's index in `questionIds` (null when unknown), and the flags and struck options still in the test. */
export function loadResumePosition(attemptId: string, questionIds: string[]): LoadedPosition {
  try {
    const raw = localStorage.getItem(storageKey(attemptId));
    if (!raw) return EMPTY;
    const stored = JSON.parse(raw) as Partial<ResumePosition> | null;
    const ids = new Set(questionIds);
    const index = typeof stored?.questionId === 'string' ? questionIds.indexOf(stored.questionId) : -1;
    const flagged = Array.isArray(stored?.flagged)
      ? stored!.flagged.filter((id): id is string => typeof id === 'string' && ids.has(id))
      : [];
    const eliminated: Record<string, string[]> = {};
    if (stored?.eliminated && typeof stored.eliminated === 'object') {
      for (const [questionId, options] of Object.entries(stored.eliminated)) {
        const list = Array.isArray(options) ? options.filter((id): id is string => typeof id === 'string') : [];
        if (ids.has(questionId) && list.length) eliminated[questionId] = list;
      }
    }
    return { index: index >= 0 ? index : null, flagged, eliminated };
  } catch {
    return EMPTY;
  }
}

export function saveResumePosition(attemptId: string, position: ResumePosition): void {
  try {
    localStorage.setItem(storageKey(attemptId), JSON.stringify(position));
  } catch {
    // Storage blocked: see above.
  }
}

export function clearResumePosition(attemptId: string): void {
  try {
    localStorage.removeItem(storageKey(attemptId));
  } catch {
    // Storage blocked: see above.
  }
}
